import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { Paper, Skeleton, Stack, Typography } from "@mui/material";
import { orgCustomersApi, useLazyGetCustomerByPhoneQuery, useUpdateCustomerMutation } from "@/features/organizations/api/orgCustomersApi";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { AnonymousUser, User } from "@/types";
import CustomerDrawer from "./CustomerDrawer";
import CustomerList from "./CustomerList";
import useSnackbar from "@/hooks/useSnackbar";
import { buildDeliveryLocation } from "@/utils/location";
import { setDeliveryLocation } from "@/features/order/slice";
import CustomerPhoneSearchField from "@/components/CustomerPhoneSearchField";

const AdminSearchCustomer = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const { values, setFieldValue } = useFormikContext<{ client: Partial<AnonymousUser | User>; deliveryLocation?: any }>();

  const [updateCustomer] = useUpdateCustomerMutation();
  const [searchUserByPhone, userByPhoneMutation] = useLazyGetCustomerByPhoneQuery();

  const [selectedId, setSelectedId] = useState<string | null>(values?.client?.id ?? null);
  const [openDrawerId, setOpenDrawerId] = useState<string | null>(null);
  const [phone, setPhone] = useState(values?.client?.phone || "");

  const results: Partial<AnonymousUser | User>[] = Array.isArray(userByPhoneMutation.data)
    ? userByPhoneMutation.data
    : userByPhoneMutation.data
      ? [userByPhoneMutation.data]
      : [];

  const handleSearch = () => {
    if (userByPhoneMutation.isLoading || phone.length < 6) {
      return;
    }
    searchUserByPhone({ organizationId, phone });
  };

  const handleClear = () => {
    setPhone("");
    setFieldValue("client", { phone: "" });
    setSelectedId(null);
    setOpenDrawerId(null);
    dispatch(orgCustomersApi.util.resetApiState());
  };

  const handleSelect = (id: string, user: any) => {
    setSelectedId(id);
    setFieldValue("client", user);
    dispatch(setDeliveryLocation(buildDeliveryLocation(user?.locations ?? [])));
  };

  const handleOpenDrawer = (id: string) => {
    setOpenDrawerId(id);
  };

  const handleUpdateClient = async () => {
    const response = await updateCustomer({
      organizationId: organizationId,
      payload: values.client,
    });

    if (response?.error) {
      openSnackbar("Error al actualizar el cliente", "error");
    } else {
      openSnackbar("Cliente actualizado", "success");
    }
  };

  useEffect(() => {
    if (values.client) {
      setFieldValue("deliveryLocation", buildDeliveryLocation(values.client?.locations ?? []));
    }
  }, [setFieldValue, values.client]);

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <CustomerPhoneSearchField
          label=""
          size="small"
          value={phone}
          onClear={handleClear}
          onSearch={handleSearch}
          onChange={(val) => setPhone(val)}
        />
      </Paper>

      {!userByPhoneMutation.isUninitialized ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h4" mb={2}>
            Resultados
          </Typography>

          {userByPhoneMutation.isLoading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="circular" width={50} height={50} />
              <Stack spacing={0} flex={0.5}>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="75%" />
              </Stack>
              <Stack flex={1} alignItems="flex-end">
                <Skeleton variant="rounded" width={100} height={50} />
              </Stack>
            </Stack>
          ) : (
            results.length === 0 ? (
              <Typography color="text.secondary">No se encontraron clientes</Typography>
            ) : (
              <CustomerList results={results} selectedId={selectedId} onSelect={handleSelect} onOpenCustomer={handleOpenDrawer} />
            )
          )}
        </Paper>
      ) : null}

      <CustomerDrawer
        isLoading={false}
        client={values.client}
        isOpen={!!openDrawerId}
        setFieldValue={setFieldValue}
        onUpdateClient={handleUpdateClient}
        onClose={() => setOpenDrawerId(null)}
      />
    </Stack>
  );
};

export default AdminSearchCustomer;
